import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { getStores, getStoreDetail, reviewProduct } from "../../api/adminService";
import { getMyProducts } from "../../api/productService";
import { ProductFormDialog } from "../BusinessOwner/Products";
import { formatCurrency, StoreTypeColors, StoreTypeLabels } from "./constants";

const STORE_PAGE_SIZE = 24;
const PRODUCT_PAGE_SIZE = 24;

const APPROVAL_APPROVED = 1;
const APPROVAL_PENDING = 2;
const APPROVAL_REJECTED = 3;

const PRODUCT_TABS = [
  { value: "all", label: "All", color: "primary", match: () => true },
  { value: "live", label: "Live", color: "success", match: (p) => p.isActive && p.approvalStatus === APPROVAL_APPROVED },
  { value: "pending", label: "Waiting for review", color: "warning", match: (p) => p.approvalStatus === APPROVAL_PENDING },
  { value: "rejected", label: "Rejected", color: "error", match: (p) => p.approvalStatus === APPROVAL_REJECTED },
  { value: "hidden", label: "Hidden by store", color: "default", match: (p) => !p.isActive },
];

// Admin view of every store's catalogue: pick a store, then open any of its products in the
// same editor Product Approvals uses. The selected store lives in ?storeId= so it survives reloads.
export default function StoreProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeId = searchParams.get("storeId");

  return storeId ? (
    <StoreProductList storeId={Number(storeId)} onBack={() => setSearchParams({})} />
  ) : (
    <StoreList onOpen={(id) => setSearchParams({ storeId: String(id) })} />
  );
}

function PageHeader({ title, subtitle, avatar, onRefresh, actions }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
        {avatar}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" noWrap>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {actions}
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function SearchBox({ value, onChange, onSubmit, placeholder }) {
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
      sx={{ p: 2 }}
    >
      <TextField
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{ minWidth: { xs: "100%", sm: 320 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
}

function StoreList({ onOpen }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [stores, setStores] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    getStores({ status: "all", search: query || undefined, page, pageSize: STORE_PAGE_SIZE })
      .then((data) => {
        setStores(data.items);
        setTotalCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load stores. Is the API running?"))
      .finally(() => setLoading(false));
  }, [query, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <PageHeader
        title="Products"
        subtitle="Choose a store to see and edit all of its products."
        avatar={
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <Inventory2OutlinedIcon />
          </Avatar>
        }
        onRefresh={load}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2.5 }}>
        <SearchBox
          value={search}
          onChange={setSearch}
          onSubmit={() => {
            setQuery(search.trim());
            setPage(1);
          }}
          placeholder="Search by store, owner or email…"
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : stores.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 8, textAlign: "center" }}>
          <StorefrontIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6">No stores found</Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" },
            }}
          >
            {stores.map((store) => (
              <Card key={store.storeId} variant="outlined">
                <CardActionArea onClick={() => onOpen(store.storeId)} sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                      <Avatar src={store.logoUrl || undefined} variant="rounded" sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                        <StorefrontIcon />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {store.storeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                          {[store.city, store.ownerName].filter(Boolean).join(" · ") || "No city set"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
                      <Chip
                        size="small"
                        color={StoreTypeColors[store.type] || "default"}
                        variant={store.type === 1 ? "outlined" : "filled"}
                        label={StoreTypeLabels[store.type] || "Retail"}
                      />
                      <Chip size="small" variant="outlined" label={`${store.productCount} products`} />
                      {store.pendingProductCount > 0 && (
                        <Chip size="small" color="warning" label={`${store.pendingProductCount} pending`} />
                      )}
                      {!store.isActive && <Chip size="small" color="error" variant="outlined" label="Inactive" />}
                      {!store.isApproved && <Chip size="small" color="warning" variant="outlined" label="Not approved" />}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
          {totalCount > STORE_PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination count={Math.ceil(totalCount / STORE_PAGE_SIZE)} page={page} onChange={(e, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function StoreProductList({ storeId, onBack }) {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // undefined = closed, null = new product, object = product being edited.
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getStoreDetail(storeId), getMyProducts(storeId)])
      .then(([storeData, productData]) => {
        setStore(storeData);
        setProducts(productData);
        setError("");
      })
      .catch(() => setError("Could not load this store's products. Is the API running?"))
      .finally(() => setLoading(false));
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const next = {};
    PRODUCT_TABS.forEach((t) => {
      next[t.value] = products.filter(t.match).length;
    });
    return next;
  }, [products]);

  const filtered = useMemo(() => {
    const matchTab = PRODUCT_TABS.find((t) => t.value === tab).match;
    const term = search.trim().toLowerCase();
    return products.filter(
      (p) =>
        matchTab(p) &&
        (!term ||
          [p.name, p.nameArabic, p.nameHebrew, p.sku, p.categoryName].some((v) => v && v.toLowerCase().includes(term))),
    );
  }, [products, tab, search]);

  const pageCount = Math.ceil(filtered.length / PRODUCT_PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PRODUCT_PAGE_SIZE, page * PRODUCT_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  function handleSaved(message) {
    setEditing(undefined);
    setNotice(message);
    load();
  }

  const needsApproval = editing && editing.approvalStatus !== APPROVAL_APPROVED;

  return (
    <Box>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ mb: 1.5 }}>
        All stores
      </Button>
      <PageHeader
        title={store?.storeName || "Store products"}
        subtitle={store ? `${products.length} products · ${StoreTypeLabels[store.type] || "Retail"}` : "Loading…"}
        avatar={
          <Avatar src={store?.logoUrl || undefined} variant="rounded" sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <StorefrontIcon />
          </Avatar>
        }
        onRefresh={load}
        actions={
          <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={!store} onClick={() => setEditing(null)}>
            Add product
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2.5, overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          {PRODUCT_TABS.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              label={
                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                  <span>{t.label}</span>
                  <Chip
                    size="small"
                    label={loading ? "…" : counts[t.value]}
                    color={tab === t.value ? t.color : "default"}
                    variant={tab === t.value ? "filled" : "outlined"}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                  />
                </Stack>
              }
              sx={{ minHeight: 52, textTransform: "none" }}
            />
          ))}
        </Tabs>
        <SearchBox value={search} onChange={setSearch} placeholder="Search by name, SKU or category…" />
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 8, textAlign: "center" }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6">No products here</Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" },
            }}
          >
            {visible.map((product) => (
              <ProductCard key={product.productId} product={product} onEdit={() => setEditing(product)} />
            ))}
          </Box>
          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

      {editing !== undefined && store && (
        <ProductFormDialog
          store={{ storeId: store.storeId, type: store.type }}
          product={editing}
          onClose={() => setEditing(undefined)}
          onSaved={handleSaved}
          showTranslations
          reviewer
          secondaryAction={
            needsApproval
              ? {
                  label: "Save & Approve",
                  color: "success",
                  onAfterSave: () => reviewProduct(editing.productId, true),
                }
              : undefined
          }
        />
      )}
    </Box>
  );
}

function ProductCard({ product, onEdit }) {
  const images = product.images || [];
  const mainImage = (images.find((i) => i.isMain) || images[0])?.imageUrl;
  const hasDiscount = product.discountPrice != null && product.discountPrice < product.price;

  return (
    <Card variant="outlined" sx={{ display: "flex", flexDirection: "column" }}>
      <CardActionArea onClick={onEdit} sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}>
        <Box sx={{ position: "relative", aspectRatio: "4 / 3", bgcolor: "background.default" }}>
          {mainImage ? (
            <Box component="img" src={mainImage} alt={product.name} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
              <ImageNotSupportedOutlinedIcon />
              <Typography variant="caption">No photos yet</Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} sx={{ position: "absolute", left: 8, top: 8 }}>
            {product.approvalStatus === APPROVAL_PENDING && <Chip size="small" color="warning" label="Pending" />}
            {product.approvalStatus === APPROVAL_REJECTED && <Chip size="small" color="error" label="Rejected" />}
            {!product.isActive && <Chip size="small" label="Hidden" sx={{ bgcolor: "rgba(0,0,0,0.55)", color: "#fff" }} />}
          </Stack>
        </Box>
        <CardContent sx={{ flexGrow: 1 }}>
          {product.categoryName && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {product.categoryName}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {product.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", mt: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} color={hasDiscount ? "error.main" : "text.primary"}>
              {formatCurrency(hasDiscount ? product.discountPrice : product.price)}
            </Typography>
            {hasDiscount && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(product.price)}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              color={product.stockQuantity > 0 ? "default" : "error"}
              label={`Stock: ${product.stockQuantity}`}
            />
            {product.variants?.length > 0 && <Chip size="small" variant="outlined" label={`${product.variants.length} variants`} />}
            {product.sku && <Chip size="small" variant="outlined" label={`SKU: ${product.sku}`} />}
          </Stack>
        </CardContent>
      </CardActionArea>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button fullWidth variant="outlined" startIcon={<EditOutlinedIcon />} onClick={onEdit}>
          Edit product
        </Button>
      </Box>
    </Card>
  );
}
